import { useMemo, useState } from "react";
import { Loader2 } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import type { Assistant, AssistantTool, ToolConfig, ToolKind } from "../../api";
import { useSaveTool } from "../../hooks";
import SettingsSheet from "../settings/SettingsSheet";
import ToolHelp from "./help";
import { toolMeta } from "./kinds";

type Props = {
  /** The whole assistant, not just its id: the help panel warns when a tool
   * can't run on the numbers this assistant actually answers on. */
  assistant: Assistant;
  kind: ToolKind;
  tool?: AssistantTool;
  open: boolean;
  onOpenChange: (open: boolean) => void;
};

const METHODS = ["POST", "GET", "PUT", "PATCH", "DELETE"];

/** A starting schema so the first API Request tool is one edit away from
 * working, rather than an empty JSON box. */
const STARTER_SCHEMA = {
  type: "object",
  properties: {
    example_field: {
      type: "string",
      description: "What the assistant should put here",
    },
  },
  required: [],
};

export default function ToolEditor({ assistant, kind, tool, open, onOpenChange }: Props) {
  const meta = toolMeta(kind);
  const saveMut = useSaveTool(assistant.id);

  const [name, setName] = useState(tool?.name ?? "");
  const [description, setDescription] = useState(tool?.description ?? "");
  const [config, setConfig] = useState<ToolConfig>(tool?.config ?? {});
  const [schemaText, setSchemaText] = useState(() =>
    JSON.stringify(
      tool?.parameters_schema && Object.keys(tool.parameters_schema).length
        ? tool.parameters_schema
        : STARTER_SCHEMA,
      null,
      2,
    ),
  );
  const [headersText, setHeadersText] = useState(() =>
    JSON.stringify(tool?.config?.headers ?? {}, null, 2),
  );

  const schemaError = useMemo(() => {
    if (kind !== "api_request") return "";
    try {
      const parsed = JSON.parse(schemaText);
      if (typeof parsed !== "object" || Array.isArray(parsed)) return "Must be a JSON object.";
      return "";
    } catch {
      return "Not valid JSON.";
    }
  }, [schemaText, kind]);

  const headersError = useMemo(() => {
    if (kind !== "api_request") return "";
    if (!headersText.trim()) return "";
    try {
      const parsed = JSON.parse(headersText);
      if (typeof parsed !== "object" || Array.isArray(parsed)) return "Must be a JSON object.";
      return "";
    } catch {
      return "Not valid JSON.";
    }
  }, [headersText, kind]);

  const set = (patch: Partial<ToolConfig>) => setConfig((c) => ({ ...c, ...patch }));

  function save() {
    const payload: Parameters<typeof saveMut.mutate>[0] = {
      id: tool?.id,
      kind,
      description: description.trim(),
      config,
    };
    if (kind === "api_request") {
      payload.name = name.trim();
      payload.parameters_schema = schemaError ? {} : JSON.parse(schemaText);
      payload.config = {
        ...config,
        headers: headersError || !headersText.trim() ? {} : JSON.parse(headersText),
      };
    }
    saveMut.mutate(payload, { onSuccess: () => onOpenChange(false) });
  }

  const blocked =
    saveMut.isPending ||
    !!schemaError ||
    !!headersError ||
    (kind === "api_request" && (!name.trim() || !(config.url || "").trim())) ||
    (kind === "transfer_call" && !(config.destination || "").trim()) ||
    (kind === "send_text" && config.recipient === "fixed" && !(config.number || "").trim()) ||
    (kind === "press_digit" &&
      config.delay_ms !== undefined &&
      !(Number.isInteger(config.delay_ms) && config.delay_ms >= 0 && config.delay_ms <= 5000));

  return (
    <SettingsSheet
      open={open}
      onOpenChange={onOpenChange}
      title={tool ? `Edit ${meta.label}` : `Add ${meta.label}`}
      description={meta.blurb}
    >
      <ToolHelp kind={kind} assistant={assistant} defaultOpen={!tool} />

      {kind === "api_request" && (
        <Field
          label="Function name"
          hint="What the model calls. Letters, numbers, dashes and underscores."
        >
          <Input
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="check_order_status"
            className="h-9 font-mono text-xs"
          />
        </Field>
      )}

      <Field
        label="When to use it"
        hint="The assistant reads this to decide. Be specific about the situation."
      >
        <Textarea
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          placeholder={
            kind === "api_request"
              ? "Look up an order by its number when the caller asks where their order is."
              : "Leave blank to use the default wording."
          }
          className="min-h-[80px] text-xs"
        />
      </Field>

      {kind === "api_request" && (
        <>
          <div className="border-t pt-4">
            <Field label="Endpoint">
              <div className="flex gap-2">
                <Select
                  value={config.method || "POST"}
                  onValueChange={(method) => set({ method })}
                >
                  <SelectTrigger className="h-9 w-28">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {METHODS.map((m) => (
                      <SelectItem key={m} value={m}>
                        {m}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Input
                  value={config.url || ""}
                  onChange={(e) => set({ url: e.target.value })}
                  placeholder="https://your-server.com/hook"
                  className="h-9 font-mono text-xs"
                />
              </div>
            </Field>
            <p className="mt-2 text-xs text-muted-foreground">
              We POST <code className="font-mono">{"{function, arguments}"}</code> and give
              the reply back to the assistant. Private and internal addresses are
              refused.
            </p>
          </div>

          <div className="border-t pt-4">
            <Field
              label="Arguments"
              hint="JSON Schema for what the model must supply."
            >
              <Textarea
                value={schemaText}
                onChange={(e) => setSchemaText(e.target.value)}
                className="min-h-[180px] font-mono text-[11px]"
                spellCheck={false}
              />
            </Field>
            {schemaError && (
              <p className="mt-1 text-xs text-destructive">{schemaError}</p>
            )}
          </div>

          <div className="border-t pt-4">
            <Field label="Headers" hint="Sent with every request, e.g. an API key.">
              <Textarea
                value={headersText}
                onChange={(e) => setHeadersText(e.target.value)}
                placeholder='{"Authorization": "Bearer ..."}'
                className="min-h-[70px] font-mono text-[11px]"
                spellCheck={false}
              />
            </Field>
            {headersError && (
              <p className="mt-1 text-xs text-destructive">{headersError}</p>
            )}
          </div>

          <div className="grid grid-cols-2 gap-3 border-t pt-4">
            <Field
              label="Signing secret"
              hint={
                tool?.config?.has_signing_secret
                  ? "Saved. Type a new one to replace it."
                  : "Optional HMAC signature."
              }
            >
              <Input
                type="password"
                value={config.signing_secret || ""}
                onChange={(e) => set({ signing_secret: e.target.value })}
                placeholder={tool?.config?.has_signing_secret ? "••••••••" : ""}
                className="h-9 font-mono text-xs"
              />
            </Field>
            <Field label="Timeout" hint="Seconds before we give up.">
              <Input
                type="number"
                min={1}
                max={30}
                value={config.timeout_seconds ?? 10}
                onChange={(e) => set({ timeout_seconds: Number(e.target.value) })}
                className="h-9"
              />
            </Field>
          </div>
        </>
      )}

      {kind === "transfer_call" && (
        <>
          <div className="border-t pt-4">
            <Field
              label="Transfer to"
              hint="In E.164 format, e.g. +14155551234. A summary of the call is texted here first."
            >
              <Input
                value={config.destination || ""}
                onChange={(e) => set({ destination: e.target.value })}
                placeholder="+14155551234"
                className="h-9 font-mono text-xs"
              />
            </Field>
          </div>
          <div className="border-t pt-4">
            <Field label="Say before transferring" hint="Leave blank for the default line.">
              <Input
                value={config.message || ""}
                onChange={(e) => set({ message: e.target.value })}
                placeholder="Connecting you to a human agent now. Please hold the line."
                className="h-9"
              />
            </Field>
          </div>
          <p className="text-xs text-muted-foreground">
            Transfers run on Telnyx and Twilio calls. A browser test session has
            no leg to transfer, so the assistant says so instead.
          </p>
        </>
      )}

      {kind === "press_digit" && (
        <div className="border-t pt-4">
          <Field
            label="Wait before pressing (ms)"
            hint="How long to let the automated prompt finish before pressing. 0 to 5000; 1000 suits most."
          >
            <Input
              type="number"
              min={0}
              max={5000}
              step={100}
              value={config.delay_ms ?? 1000}
              onChange={(e) =>
                set({ delay_ms: e.target.value === "" ? undefined : Number(e.target.value) })
              }
              className="h-9 w-32"
            />
          </Field>
        </div>
      )}

      {kind === "end_call" && (
        <div className="border-t pt-4">
          <Field
            label="Closing line"
            hint="Spoken and allowed to finish before the line drops. Optional — the assistant usually says its own goodbye."
          >
            <Input
              value={config.message || ""}
              onChange={(e) => set({ message: e.target.value })}
              placeholder="Thanks for calling. Goodbye!"
              className="h-9"
            />
          </Field>
        </div>
      )}

      {kind === "send_text" && (
        <>
          <div className="border-t pt-4">
            <Field label="Send to">
              <Select
                value={config.recipient || "caller"}
                onValueChange={(recipient) =>
                  set({ recipient: recipient as ToolConfig["recipient"] })
                }
              >
                <SelectTrigger className="h-9">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="caller">The caller's own number</SelectItem>
                  <SelectItem value="fixed">A fixed number</SelectItem>
                </SelectContent>
              </Select>
            </Field>
          </div>
          {config.recipient === "fixed" && (
            <Field label="Number">
              <Input
                value={config.number || ""}
                onChange={(e) => set({ number: e.target.value })}
                placeholder="+14155551234"
                className="h-9 font-mono text-xs"
              />
            </Field>
          )}
          <p className="text-xs text-muted-foreground">
            Sent from this workspace's Telnyx number — the same sender the
            post-call recap uses.
          </p>
        </>
      )}

      {(kind === "calcom" || kind === "odoo") && (
        <p className="border-t pt-4 text-xs text-muted-foreground">
          Uses this workspace's {kind === "calcom" ? "Cal.com" : "Odoo"} connection
          from Settings → Integrations. Until it's connected, the assistant is not
          offered these functions at all — so it can't promise something it can't
          do.
        </p>
      )}

      <div className="sticky bottom-0 -mx-5 mt-2 border-t bg-background px-5 py-3">
        <div className="flex justify-end gap-2">
          <Button variant="outline" size="sm" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button size="sm" disabled={blocked} onClick={save}>
            {saveMut.isPending && <Loader2 className="size-3.5 animate-spin" />}
            {tool ? "Save" : "Add tool"}
          </Button>
        </div>
      </div>
    </SettingsSheet>
  );
}

function Field({
  label,
  hint,
  children,
}: {
  label: string;
  hint?: string;
  children: React.ReactNode;
}) {
  return (
    <div className="space-y-1.5">
      <Label className="text-sm">{label}</Label>
      {children}
      {hint && <p className="text-xs text-muted-foreground">{hint}</p>}
    </div>
  );
}
